import React from 'react';
import { PostItemFragment } from '../../../models/graphql';
import { ContainerListItem } from '../../actions/ListItem/ListItem.view';
import { AttributeIconsEnum } from '../../../constants/attribute-icons';
import { BasePostCardPortraitView } from './BasePostCardPortrait.view';
import { BasePostCardLandscapeView } from './BasePostCardLandscape.view';
import { BasePostCardCalendarView } from './BasePostCardCalendar.view';
import {
  DuplicateOrReschedulePostHandler,
  PostScheduleViewContext
} from '../../../modules/Publish/PostSchedule/PostSchedule.controller';
import { UseFormReturn } from 'react-hook-form';
import classNames from 'classnames';
import classes from './BasePostCard.module.scss';
import { useDidMount } from '../../../hooks/useDidMount';
import { DraggableValues } from '../../../constants/drag-and-drop';
import { Checkbox } from '../../forms/Checkbox';

export type Attributes = {
  icon: AttributeIconsEnum;
  label: string;
  toolTip?: string | JSX.Element;
};

export type PostError = {
  message: string;
  actionLabel: string;
  action: () => void;
};

export enum CardOrientationEnum {
  LANDSCAPE = 'Landscape',
  PORTRAIT = 'Portrait',
  CALENDAR = 'Calendar'
}

export type DraggablePostCardProps = {
  isDraggable: true;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
};

export type StaticPostCardProps = {
  isDraggable?: false;
  moveCard?: never;
};

export type UseFormPostCardProps =
  | {
      hideCheckbox?: false;
      useFormInstance: UseFormReturn;
    }
  | {
      hideCheckbox: true;
      useFormInstance?: never;
    };

export type PostCardProps = (DraggablePostCardProps | StaticPostCardProps) & UseFormPostCardProps;

export type PostCardViewProps = PostCardProps & {
  index: number;
  context: PostScheduleViewContext;
  post: PostItemFragment;
  menuListItems: ContainerListItem[];
  orientation: CardOrientationEnum;
  hasHoverBackground?: boolean;
  isPostStatusUnqueued?: boolean;
  reschedulePostHandler?: DuplicateOrReschedulePostHandler;
  openDetailsModal?: VoidFunction;
  isDetailedPastPost?: boolean;
  animateCard?: boolean;
  showBarGraph?: boolean;
  handlerId?: DraggableValues | null;
};

export const BasePostCardView = (props: PostCardViewProps): JSX.Element => {
  const { index, post, orientation, hideCheckbox, useFormInstance, reschedulePostHandler, context } = props;
  const isStacked = post.variationMeta?.hasMultiple || false;

  // Determine which post card layout/style to use based on CardOrientationEnum
  const PostCard =
    orientation === CardOrientationEnum.CALENDAR
      ? BasePostCardCalendarView
      : orientation === CardOrientationEnum.PORTRAIT
      ? BasePostCardPortraitView
      : BasePostCardLandscapeView;

  const cardProps = {
    ...props,
    isStacked,
    reschedulePostHandler
  };

  const containerClasses = classNames('grid-x', classes.BasePostCard, {
    [classes.BasePostCardLandscape]: orientation === CardOrientationEnum.LANDSCAPE,
    [classes.BasePostCardLandscapeHasNoCheckBox]: hideCheckbox
  });

  // When the card mounts, and we have a checkbox, make sure we force set the value so it bubbles up to CheckAllCheckboxes
  useDidMount(() => {
    if (useFormInstance) {
      const currentValue = useFormInstance.getValues(`postItemCheckBox.${index}`);
      // If there is a value other than a falsy, do nothing
      if (!currentValue) {
        useFormInstance.setValue(`postItemCheckBox.${index}`, false);
      }
    }
  });

  return (
    <div className={containerClasses}>
      {orientation === CardOrientationEnum.PORTRAIT && (
        <div className={'cell shrink'}>
          {!hideCheckbox && (
            <div className={classes.PostCardCheckboxWrapper}>
              <Checkbox
                key={`post-${context}-${post.id}-checkbox`}
                name={`postItemCheckBox.${index}`}
                size={'large'}
                formInstance={useFormInstance}
                rules={{}}
                value={post.id}
              />
            </div>
          )}
        </div>
      )}
      <div className={'cell auto'}>
        <PostCard {...cardProps} />
      </div>
    </div>
  );
};
